import React, { useState, useRef, useEffect, useMemo } from "react";
import TreatmentCard from "../../molecules/TreatmentCard/TreatmentCard";
import treatmentsData from "../../../constants/treatmentsData";
import Text from "../../atoms/Text/Text";
import BookingButton from "../../molecules/BookingButton/BookingButton";
import { useTranslation } from "react-i18next";
import TreatmentTabs from "../../molecules/TreatmentTabs/TreatmentTabs";
import "./treatment.scss";

const TABS = [
  { id: "headSpa", i18nKey: "treatments.tabs.headSpa" },
  { id: "massages", i18nKey: "treatments.tabs.massages" },
  { id: "mesotherapy", i18nKey: "treatments.tabs.mesotherapy" },
  { id: "combiDeals", i18nKey: "treatments.tabs.combiDeals" },
];

const Treatment = () => {
  const listRef = useRef(null);
  const { t } = useTranslation();

  const [focusedIndex, setFocusedIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState("headSpa");

  // Filter behandelingen op basis van actieve tab.
  // Bestaande behandelingen zonder `category` tellen als Head Spa.
  const filteredTreatments = useMemo(
    () =>
      treatmentsData.filter((treatment) => {
        if (activeCategory === "headSpa") {
          return !treatment.category || treatment.category === "headSpa";
        }
        return treatment.category === activeCategory;
      }),
    [activeCategory]
  );

  // Reset focus en scrollpositie als de tab verandert
  useEffect(() => {
    setFocusedIndex(0);
    if (listRef.current) {
      listRef.current.scrollLeft = 0;
    }
  }, [activeCategory]);

  // Zorg dat de "gefocusde" kaart ongeveer in het midden blijft bij scroll/resize
  useEffect(() => {
    const listElement = listRef.current;

    if (!listElement || !filteredTreatments.length) {
      setFocusedIndex(0);
      return;
    }

    const updateFocusedCard = () => {
      if (!listRef.current) return;

      const scrollLeft = listRef.current.scrollLeft;
      const clientWidth = listRef.current.clientWidth;
      const scrollWidth = listRef.current.scrollWidth;

      const firstCard = listRef.current.firstChild;
      if (!firstCard) return;

      const cardStyle = window.getComputedStyle(firstCard);
      const cardWidth = firstCard.offsetWidth;
      const cardMarginLeft = parseFloat(cardStyle.marginLeft) || 0;
      const cardMarginRight = parseFloat(cardStyle.marginRight) || 0;
      const totalCardWidth = cardWidth + cardMarginLeft + cardMarginRight;

      const centerPosition = scrollLeft + clientWidth / 2;
      let index = Math.floor(centerPosition / totalCardWidth);

      if (scrollLeft === 0) {
        index = 0;
      } else if (scrollLeft + clientWidth >= scrollWidth) {
        index = filteredTreatments.length - 1;
      }

      if (index < 0) index = 0;
      if (index >= filteredTreatments.length) {
        index = filteredTreatments.length - 1;
      }

      setFocusedIndex(index);
    };

    updateFocusedCard();

    listElement.addEventListener("scroll", updateFocusedCard, { passive: true });
    window.addEventListener("resize", updateFocusedCard);

    return () => {
      listElement.removeEventListener("scroll", updateFocusedCard);
      window.removeEventListener("resize", updateFocusedCard);
    };
  }, [filteredTreatments.length]);

  const handleMouseEnter = (index) => {
    setFocusedIndex(index);
  };

  const handleSwipeLeft = (index) => {
    if (index > 0) {
      setFocusedIndex(index - 1);
    }
  };

  const handleSwipeRight = (index) => {
    if (index < filteredTreatments.length - 1) {
      setFocusedIndex(index + 1);
    }
  };

  const handleTabClick = (tabId) => {
    setActiveCategory(tabId);
  };

  return (
    <section
      id="treatments"
      className="treatments"
      aria-labelledby="treatments-heading"
    >
      <div className="treatments__text">
        <header>
          <Text variant="h1" className="treatments-header">
            {t("treatments.header")}
          </Text>
        </header>
        <Text variant="p" className="treatment-info">
          {t("treatments.info")}
        </Text>
      </div>

      {/* Tabs */}
      <TreatmentTabs
        tabs={TABS}
        activeTab={activeCategory}
        onTabClick={handleTabClick}
      />

      <div
        className="treatments__container"
        id="treatmentslist"
        role="tabpanel"
        aria-live="polite"
        aria-labelledby={`treatments-tab-${activeCategory}`}
      >
        {/* Duo-tekst alleen bij Head Spa */}
        {activeCategory === "headSpa" && (
          <Text variant="p" className="treatments__duo">
            {t("treatments.duo")}
          </Text>
        )}

        <div className="treatments__list" ref={listRef}>
          {filteredTreatments.length ? (
            filteredTreatments.map((treatment, index) => (
              <TreatmentCard
                key={treatment.title}
                title={treatment.title}
                i18nTitle={treatment.i18nTitle}
                description={treatment.description}
                subdescription={treatment.subdescription}
                rules={treatment.rules}
                rules2={treatment.rules2}
                duration={treatment.duration}
                price={treatment.price}
                steps={treatment.steps}
                link={treatment.link}
                showDurationIcon={treatment.duration != null}
                onSwipeLeft={() => handleSwipeLeft(index)}
                onSwipeRight={() => handleSwipeRight(index)}
                index={index}
                isFocused={focusedIndex === index}
                totalTreatments={filteredTreatments.length}
                onMouseEnter={() => handleMouseEnter(index)}
              />
            ))
          ) : (
            <p className="treatments__empty-message">
              {t("treatments.empty")}
            </p>
          )}
        </div>
      </div>

      <BookingButton link="https://headwellness.zohobookings.eu/portal-embed#/customer/headwellness" showCategoryModal={true} />
    </section>
  );
};

export default Treatment;
